import React, { useEffect, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { formatLongDate, formatTimeWithAmPm } from "@root/utils/date-helper";
import { CalendarIcon } from "@root/assets";
import { MailIcon } from "@root/assets/icons/MailIcon";
import { ImageIcon } from "@root/assets/icons/ImageIcon";
import { PasswordIcon } from "@root/assets/icons/PasswordIcon";
import { OrgIcon } from "@root/assets/icons/OrgIcon";
import { UserIcon } from "@root/assets/icons/UserIcon";
import {
  PAGINATION_DEFAULTS,
  PROFILE_CHANGE_EVENT_TYPES,
} from "@root/constants";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Paginate } from "@root/components/pagination";
import { useGlobalContext } from "@root/context/global-context";

const iconMap: Record<string, any> = {
  email: MailIcon,
  profile_picture: ImageIcon,
  password: PasswordIcon,
  organization: OrgIcon,
  name: UserIcon,
};

export const ProfileChangeHistoryDrawer = ({
  isOpen,
  onClose,
  viewHistoryChange,
  userId,
}: any) => {
  const { t } = useTranslation("common");
  const user: any = useSelector(getLoggedInUser);
  const { historyCount, userHistory, historyPage, setHistoryPage } =
    useGlobalContext();

  useEffect(() => {
    if (isOpen && userId && historyPage > 1) {
      // Only fetch on page change, not initial open (initial is handled by parent)
      // const payload = {
      //   id: userId,
      //   limit: PAGINATION_DEFAULTS.PAGESIZE,
      //   offset: (historyPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      // };
      viewHistoryChange(userId);
    }
  }, [historyPage]);

  function handlePageChange(newPage: number) {
    setHistoryPage(newPage);
  }

  // Flatten the array if it's an array of arrays
  let flatHistory = [];
  if (Array.isArray(userHistory) && userHistory.length > 0) {
    if (Array.isArray(userHistory[0])) {
      flatHistory = userHistory.flat();
    } else {
      flatHistory = userHistory;
    }
  }
  useEffect(() => {
    if (!isOpen) {
      setHistoryPage(1);
    }
  }, [isOpen]);

  // Map 'field' to event type and label
  const fieldToType = {
    email: PROFILE_CHANGE_EVENT_TYPES.EMAIL,
    avatar: PROFILE_CHANGE_EVENT_TYPES.PROFILE_PICTURE,
    password: PROFILE_CHANGE_EVENT_TYPES.PASSWORD,
    subscriberId: PROFILE_CHANGE_EVENT_TYPES.ORGANIZATION,
    companyName: PROFILE_CHANGE_EVENT_TYPES.ORGANIZATION,
    language: PROFILE_CHANGE_EVENT_TYPES.LANGUAGE,
    phone: PROFILE_CHANGE_EVENT_TYPES.PHONE,
    fullName: PROFILE_CHANGE_EVENT_TYPES.NAME,
    name: PROFILE_CHANGE_EVENT_TYPES.NAME,
  };

  const normalized = flatHistory?.map((item: any) => ({
    type: fieldToType[item?.field as keyof typeof fieldToType] || item?.field,
    oldValue: item?.oldValue,
    newValue: item?.newValue,
    date: item?.createdAt,
    field: item?.field,
  }));

  // Filter out items with invalid dates and ensure all required fields exist
  const validItems =
    normalized?.filter(
      (item: any) =>
        item?.date &&
        typeof item.date === "string" &&
        item.date.includes("T") &&
        !isNaN(new Date(item.date).getTime()) &&
        item?.field && // Ensure field exists
        item?.type // Ensure type exists
    ) || [];

  const groupedObj = validItems.reduce((acc: any, item: any) => {
    const date = item.date?.split("T")[0];
    if (!acc[date]) acc[date] = [];
    acc[date]?.push(item);
    return acc;
  }, {});
  const grouped = Object?.entries(groupedObj)?.map(([date, events]) => ({
    date,
    events,
  }));

  const renderTypes = (group: any) => {
    return (
      <VStack align="stretch" spacing={4}>
        {group?.events?.map((event: any, idx: number) => (
          <Flex
            key={idx}
            p={4}
            borderRadius="12px"
            bg="transparent"
            border={"1px solid #CDD6DC"}
            boxShadow="sm"
            alignItems={"center"}
            gap={7}
            flexDirection={"row"}
          >
            <Icon as={iconMap[event?.type]} />
            <Flex
              gap={1}
              alignItems={"flex-start"}
              flexDirection={"column"}
              flex={1}
            >
              <Text fontWeight="medium">
                {t(`profileChangeHistory.eventLabelMap.${event?.type}`) ||
                  event?.type}
              </Text>
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.EMAIL && (
                <Text
                  fontSize="14px"
                  fontWeight={"500"}
                  color="#5C6670"
                  as="span"
                >
                  {t("profileChangeHistory.from")}:{" "}
                  <Text as="span" fontWeight="500" color="#2F2F34">
                    {event?.oldValue}
                  </Text>{" "}
                  &rarr; {t("profileChangeHistory.to")}:{" "}
                  <Text as="span" fontWeight="500" color="#2F2F34">
                    {event?.newValue}
                  </Text>
                </Text>
              )}
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.NAME && (
                <Text
                  fontSize="14px"
                  fontWeight={"500"}
                  color="#5C6670"
                  as="span"
                >
                  {t("profileChangeHistory.from")}:{" "}
                  <Text as="span" fontWeight="500" color="#2F2F34">
                    {event?.oldValue}
                  </Text>{" "}
                  &rarr; {t("profileChangeHistory.to")}:{" "}
                  <Text as="span" fontWeight="500" color="#2F2F34">
                    {event?.newValue}
                  </Text>
                </Text>
              )}
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.LANGUAGE && (
                <Text fontSize="14px" fontWeight={"500"} color="#5C6670">
                  {t("profileChangeHistory.languageChanged")}
                </Text>
              )}
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.PHONE && (
                <Text
                  fontSize="14px"
                  fontWeight={"500"}
                  color="#5C6670"
                  as="span"
                >
                  {t("profileChangeHistory.from")}:{" "}
                  <Text as="span" fontWeight="500" color="#2F2F34">
                    {event?.oldValue}
                  </Text>{" "}
                  &rarr; {t("profileChangeHistory.to")}:{" "}
                  <Text as="span" fontWeight="500" color="#2F2F34">
                    {event?.newValue}
                  </Text>
                </Text>
              )}
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.PROFILE_PICTURE && (
                <Text
                  fontSize="14px"
                  fontWeight={"500"}
                  color="#5C6670"
                  width={"100%"}
                >
                  {t("profileChangeHistory.uploadedNewProfilePicture")}{" "}
                  {event?.newValue ? (
                    event?.newValue?.startsWith("http") ? (
                      <a
                        href={event?.newValue}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2F2F34",
                        }}
                      >
                        {event?.newValue?.split("/")?.pop()}
                      </a>
                    ) : (
                      <span
                        style={{
                          color: "#2F2F34",
                          cursor: "pointer",
                        }}
                      >
                        {event?.newValue}
                      </span>
                    )
                  ) : null}
                </Text>
              )}
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.ORGANIZATION && (
                <Text fontSize="14px" fontWeight={"500"} color="#5C6670">
                  {event?.field === PROFILE_CHANGE_EVENT_TYPES.COMPANY_NAME && (
                    <>
                      {t("profileChangeHistory.from")}: {event?.oldValue} &rarr;{" "}
                      {t("profileChangeHistory.to")}: {event?.newValue}
                    </>
                  )}
                </Text>
              )}
              {event?.type === PROFILE_CHANGE_EVENT_TYPES.PASSWORD && (
                <Text fontSize="14px" fontWeight={"500"} color="#5C6670">
                  {t("profileChangeHistory.passwordChanged")}
                </Text>
              )}
            </Flex>

            <Text fontSize="sm" color="gray.500" minW="60px" textAlign="right">
              {event?.date ? formatTimeWithAmPm(event?.date) : "--"}
            </Text>
          </Flex>
        ))}
      </VStack>
    );
  };

  const renderDataByDate = () => {
    return (
      <VStack align="stretch" spacing={8}>
        {grouped && grouped?.length > 0 ? (
          grouped?.map((group: any) => (
            <Box key={group?.date}>
              <Flex mt={3} mb={3} gap={2} alignItems={"center"}>
                <CalendarIcon />
                <Text fontWeight="600" fontSize={"16px"} color="#2F2F34">
                  {group?.date ? formatLongDate(group?.date) : "Invalid Date"}
                </Text>
              </Flex>
              {renderTypes(group)}
            </Box>
          ))
        ) : (
          <Text color="gray.400" textAlign="center">
            {t("profileChangeHistory.noHistory")}
          </Text>
        )}
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent
        bg={"white"}
        height="100vh"
        maxHeight="100vh"
        overflow={"auto"}
        minH={"100vh"}
        style={{
          height: "100vh",
          maxHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DrawerCloseButton />
        <DrawerHeader>
          <Text fontWeight="600" fontSize="24px" textColor={"#2F2F34"}>
            {t("profileChangeHistory.title")}
          </Text>
          <Text fontSize="16px" fontWeight={"400"} color="#798692">
            {t("profileChangeHistory.subtitle")}
          </Text>
        </DrawerHeader>
        <DrawerBody
          overflowY="auto"
          minH={"90vh"}
          maxH={"90vh"}
          h={"90vh"}
          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
          className="custom-scrollable-box"
        >
          {renderDataByDate()}
        </DrawerBody>
        <Paginate
          page={historyPage}
          count={historyCount}
          pageSize={PAGINATION_DEFAULTS.PAGESIZE}
          onPageChange={handlePageChange}
        />
      </DrawerContent>
    </Drawer>
  );
};
